import React, { useEffect, useState } from 'react';
import { Icon } from '@iconify/react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { encodeData, decodeData } from '../../../utils/encodeAndEncode';
import { getCourseApprovalANDApprovedANDRejected, approveCourse, RejectApprovalCourse } from '../../../services/adminService';
import Loader from '../../../components/common/Loader';
import { usePermissions } from '../../../context/PermissionsContext.js';

const defaultCourseImage = '/images/admin/course/course1.png';

function PendingApproval({ searchTerm = '', setSearchTerm, registerRefresh }) {
    const navigate = useNavigate();
    const { permissions } = usePermissions();
    const [approvalRequestsData, setApprovalRequestsData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [showRejectModal, setShowRejectModal] = useState(false);
    const [showApproveModal, setShowApproveModal] = useState(false);
    const [selectedCourseId, setSelectedCourseId] = useState(null);
    const [selectedCourseName, setSelectedCourseName] = useState('');
    const [rejectionReason, setRejectionReason] = useState('');
    const [processing, setProcessing] = useState(false);

    const getApprovalRequests = async () => {
        try {
            setLoading(true);
            const response = await getCourseApprovalANDApprovedANDRejected();
            const pendingCourses = response.data.approve || [];
            setApprovalRequestsData(pendingCourses);
        } catch (error) {
            console.error('Error fetching approval requests:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        getApprovalRequests();
    }, []);

    useEffect(() => {
        if (registerRefresh) registerRefresh(getApprovalRequests);
    }, [registerRefresh]);

    // Reset to first page when search term changes
    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm]);

    const openApproveModal = (courseId, courseName) => {
        if (!permissions.course_approval_workflow_approve) {
            toast.warning("You don't have permission to approve courses");
            return;
        }
        setSelectedCourseId(courseId);
        setSelectedCourseName(courseName);
        setShowApproveModal(true);
    };

    const handleApprove = async () => {
        try {
            setProcessing(true);
            await approveCourse({ id: selectedCourseId });
            // Remove the approved course from the list
            setApprovalRequestsData(prev => prev.filter(course => course.id !== selectedCourseId));
            setShowApproveModal(false);
            setSelectedCourseId(null);
            setSelectedCourseName('');
            // Show success message
            toast.success('Course approved successfully!');
        } catch (error) {
            console.error('Error approving course:', error);
            toast.error('Failed to approve course. Please try again.');
        } finally {
            setProcessing(false);
        }
    };

    const handleReject = async () => {
        if (!rejectionReason.trim()) {
            toast.warning('Please provide a rejection reason');
            return;
        }

        try {
            setProcessing(true);
            await RejectApprovalCourse({ 
                id: selectedCourseId, 
                reason: rejectionReason 
            });
            // Remove the rejected course from the list
            setApprovalRequestsData(prev => prev.filter(course => course.id !== selectedCourseId));
            // Close modal and reset states
            setShowRejectModal(false);
            setSelectedCourseId(null);
            setSelectedCourseName('');
            setRejectionReason('');
            // Show success message
            toast.success('Course rejected successfully!');
        } catch (error) {
            console.error('Error rejecting course:', error);
            toast.error('Failed to reject course. Please try again.');
        } finally {
            setProcessing(false);
        }
    };

    const openRejectModal = (courseId, courseName) => {
        if (!permissions.course_approval_workflow_reject) {
            toast.warning("You don't have permission to reject courses");
            return;
        }
        setSelectedCourseId(courseId);
        setSelectedCourseName(courseName);
        setShowRejectModal(true);
    };

    const getStatusClass = (status) => {
        return 'inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-blue-50 border border-blue-200 text-blue-700';
    }

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    }

    // Search functionality
    const filteredData = approvalRequestsData.filter(item => {
        const searchString = searchTerm.toLowerCase();
        return (
            item.course_name?.toLowerCase().includes(searchString) ||
            item.trainer_name?.toLowerCase().includes(searchString) ||
            item.trainer_email?.toLowerCase().includes(searchString) ||
            item.course_desc?.toLowerCase().includes(searchString)
        );
    });

    // Pagination logic
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = filteredData.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredData.length / itemsPerPage);

    // Page navigation
    const handlePageChange = (pageNumber) => {
        if (pageNumber >= 1 && pageNumber <= totalPages) {
            setCurrentPage(pageNumber);
        }
    };

    // Items per page change handler
    const handleItemsPerPageChange = (e) => {
        const newItemsPerPage = parseInt(e.target.value);
        setItemsPerPage(newItemsPerPage);
        setCurrentPage(1); // Reset to first page when changing items per page
    };

    const handleViewCourse = (courseId, e) => {
        // Prevent row click when clicking on action buttons
        if (e && e.target.closest('.action-buttons')) {
            return;
        }
        const encodedCourseId = encodeData(courseId);
        navigate(`/admin/approvalRequest/view/${encodedCourseId}`);
    };

    return (
        <>
            {/* Premium Table Container */}
            <div className="bg-white rounded-[24px] border border-gray-100 shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="bg-gray-50/50 border-b border-gray-100">
                                <th className="px-6 py-4 text-[11px] font-bold text-gray-400 uppercase tracking-widest w-[45%]">Course Details</th>
                                <th className="px-6 py-4 text-[11px] font-bold text-gray-400 uppercase tracking-widest w-40">Submission</th>
                                <th className="px-6 py-4 text-[11px] font-bold text-gray-400 uppercase tracking-widest w-40">Instructor</th>
                                <th className="px-6 py-4 text-[11px] font-bold text-gray-400 uppercase tracking-widest text-center w-44">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50">
                            {loading ? (
                                <tr>
                                    <td colSpan="4" className="px-6 py-24 text-center">
                                        <div className="flex flex-col items-center">
                                            <div className="w-10 h-10 border-4 border-[#7083FF]/20 border-t-[#7083FF] rounded-full animate-spin mb-4" />
                                            <p className="text-gray-400 font-bold text-xs uppercase tracking-widest">Scanning Catalog...</p>
                                        </div>
                                    </td>
                                </tr>
                            ) : currentItems.length === 0 ? (
                                <tr>
                                    <td colSpan="4" className="px-6 py-24 text-center">
                                        <div className="flex flex-col items-center max-w-sm mx-auto">
                                            <div className="w-20 h-20 bg-gray-50 rounded-full flex items-center justify-center mb-6">
                                                <Icon icon="fluent:clipboard-task-list-ltr-24-regular" className="text-gray-200" width={40} />
                                            </div>
                                            <h3 className="text-xl font-bold text-gray-900 mb-2">
                                                {searchTerm ? 'No matches found' : 'Clear Skies'}
                                            </h3>
                                            <p className="text-gray-500 text-sm leading-relaxed">
                                                {searchTerm
                                                    ? `We couldn't find any courses matching "${searchTerm}". Try broadening your search.`
                                                    : 'There are currently no course approval requests pending your review.'}
                                            </p>
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                currentItems.map((request) => (
                                    <tr 
                                        key={request.id} 
                                        onClick={(e) => handleViewCourse(request.id, e)}
                                        className="hover:bg-gray-50/80 transition-all duration-300 group cursor-pointer"
                                    >
                                        <td className="px-6 py-6">
                                            <div className="flex gap-5">
                                                <div className="relative shrink-0">
                                                    <img
                                                        src={request.banner_image || defaultCourseImage}
                                                        alt={request.course_name}
                                                        className="w-44 h-28 rounded-2xl object-cover border border-gray-100 shadow-sm transition-transform duration-500 group-hover:scale-[1.02]"
                                                        onError={(e) => { e.target.src = defaultCourseImage; }}
                                                    />
                                                    <div className="absolute top-2 right-2">
                                                        <span className="px-2 py-1 rounded-lg bg-white/90 backdrop-blur-md border border-white/50 text-[10px] font-black text-gray-900 shadow-sm">
                                                            {request.course_summary.total_duration?.split(':')[0].padStart(2, '0')}h {request.course_summary.total_duration?.split(':')[1].padStart(2, '0')}m
                                                        </span>
                                                    </div>
                                                </div>
                                                <div className="min-w-0 flex flex-col justify-between py-0.5">
                                                    <div>
                                                        <h4 className="font-bold text-gray-900 text-base leading-snug line-clamp-1 group-hover:text-[#7083FF] transition-colors">
                                                            {request.course_name}
                                                        </h4>
                                                        <div className="flex flex-wrap gap-x-4 gap-y-2 mt-2">
                                                            <div className="flex items-center gap-1.5 text-gray-400">
                                                                <Icon icon="fluent:video-24-regular" className="text-[#7083FF]" width={14} />
                                                                <span className="text-[11px] font-bold uppercase tracking-tight">{request.course_summary.total_videos} Videos</span>
                                                            </div>
                                                            <div className="flex items-center gap-1.5 text-gray-400 border-l border-gray-100 pl-4">
                                                                <Icon icon="fluent:document-24-regular" className="text-[#7083FF]" width={14} />
                                                                <span className="text-[11px] font-bold uppercase tracking-tight">{request.course_summary.total_documents} Docs</span>
                                                            </div>
                                                            <div className="flex items-center gap-1.5 text-gray-400 border-l border-gray-100 pl-4">
                                                                <Icon icon="fluent:task-list-24-regular" className="text-[#7083FF]" width={14} />
                                                                <span className="text-[11px] font-bold uppercase tracking-tight">{request.course_summary.total_assessments} Tests</span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="mt-auto">
                                                        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg bg-orange-50 text-orange-600 text-[10px] font-black uppercase tracking-widest border border-orange-100/50">
                                                            <div className="w-1.5 h-1.5 rounded-full bg-orange-500 animate-pulse" />
                                                            {request.status}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-6 align-middle">
                                            <div className="flex flex-col">
                                                <span className="text-sm font-bold text-gray-900">{formatDate(request.request_date)}</span>
                                                <span className="text-[11px] text-gray-400 font-medium mt-0.5">Submitted</span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-6 align-middle">
                                            <div className="flex items-center gap-3">
                                                <div className="w-9 h-9 rounded-xl bg-gray-50 border border-gray-100 flex items-center justify-center text-[#7083FF] font-bold text-xs uppercase">
                                                    {request.trainer_name?.charAt(0) || 'U'}
                                                </div>
                                                <div className="flex flex-col min-w-0">
                                                    <span className="text-sm font-bold text-gray-900 truncate">{request.trainer_name}</span>
                                                    <span className="text-[11px] text-gray-400 font-medium truncate">Instructor</span>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-6 align-middle">
                                            <div className="flex items-center justify-center gap-2 action-buttons opacity-100 sm:opacity-0 group-hover:opacity-100 transition-all duration-300">
                                                <button
                                                    onClick={(e) => { e.stopPropagation(); openApproveModal(request.id, request.course_name); }}
                                                    disabled={processing || !permissions.course_approval_workflow_approve}
                                                    className="p-3 rounded-xl bg-emerald-50 text-emerald-600 hover:bg-emerald-500 hover:text-white transition-all shadow-sm border border-emerald-100/50 flex items-center justify-center"
                                                    title="Approve Submission"
                                                >
                                                    <Icon icon="fluent:checkmark-24-regular" width={18} />
                                                </button>
                                                <button
                                                    onClick={(e) => { e.stopPropagation(); openRejectModal(request.id, request.course_name); }}
                                                    disabled={processing || !permissions.course_approval_workflow_reject}
                                                    className="p-3 rounded-xl bg-red-50 text-red-600 hover:bg-red-500 hover:text-white transition-all shadow-sm border border-red-100/50 flex items-center justify-center"
                                                    title="Decline Submission"
                                                >
                                                    <Icon icon="fluent:dismiss-24-regular" width={18} />
                                                </button>
                                                <button
                                                    onClick={(e) => { e.stopPropagation(); handleViewCourse(request.id); }}
                                                    className="p-3 rounded-xl bg-white text-gray-500 hover:text-gray-900 hover:bg-gray-50 transition-all shadow-sm border border-gray-100 flex items-center justify-center"
                                                    title="Curriculum Preview"
                                                >
                                                    <Icon icon="fluent:eye-24-regular" width={18} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Premium Pagination Container */}
            {!loading && filteredData.length > 0 && (
                <div className="mt-8 flex flex-col sm:flex-row items-center justify-between gap-6 px-6 py-4 bg-white rounded-3xl border border-gray-100 shadow-sm">
                    <div className="text-sm text-gray-500 font-medium">
                        Showing <span className="font-black text-gray-900">{indexOfFirstItem + 1}</span>–<span className="font-black text-gray-900">{Math.min(indexOfLastItem, filteredData.length)}</span> of <span className="font-black text-gray-900">{filteredData.length}</span> entries
                    </div>

                    <div className="flex items-center gap-4">
                        <div className="flex items-center gap-2">
                            <span className="text-[11px] font-bold text-gray-400 uppercase tracking-wider">Per Page</span>
                            <select
                                value={itemsPerPage}
                                onChange={handleItemsPerPageChange}
                                className="px-3 py-2 rounded-xl border border-gray-100 bg-gray-50/50 text-xs font-bold text-gray-900 focus:outline-none focus:ring-4 focus:ring-[#7083FF]/5 transition-all"
                            >
                                <option value={10}>10</option>
                                <option value={20}>20</option>
                                <option value={50}>50</option>
                            </select>
                        </div>

                        <div className="flex items-center gap-1.5 ml-2 border-l border-gray-100 pl-4">
                            <button
                                type="button"
                                onClick={() => handlePageChange(currentPage - 1)}
                                disabled={currentPage === 1}
                                className="p-2.5 rounded-xl border border-gray-100 text-gray-600 hover:bg-gray-50 disabled:opacity-30 transition-all"
                            >
                                <Icon icon="fluent:chevron-left-24-regular" width={18} />
                            </button>
                            <div className="px-4 text-xs font-black text-gray-900 uppercase tracking-widest bg-gray-50 py-2.5 rounded-xl border border-gray-100">
                                {currentPage} <span className="text-gray-400 font-bold mx-1">/</span> {totalPages}
                            </div>
                            <button
                                type="button"
                                onClick={() => handlePageChange(currentPage + 1)}
                                disabled={currentPage === totalPages}
                                className="p-2.5 rounded-xl border border-gray-100 text-gray-600 hover:bg-gray-50 disabled:opacity-30 transition-all"
                            >
                                <Icon icon="fluent:chevron-right-24-regular" width={18} />
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modals Enhancement */}
            {showApproveModal && (
                <div className="fixed inset-0 bg-black/40 backdrop-blur-md flex items-center justify-center z-[70] p-4 animate-in fade-in duration-300">
                    <div className="bg-white rounded-[32px] shadow-2xl max-w-md w-full overflow-hidden animate-in zoom-in-95 duration-300 border border-white/20">
                        <div className="px-8 py-6 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center">
                                    <Icon icon="fluent:checkmark-circle-24-regular" width={24} />
                                </div>
                                <h5 className="text-lg font-black text-gray-900 tracking-tight">Approve Course</h5>
                            </div>
                            <button
                                type="button"
                                className="w-10 h-10 rounded-xl flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-all"
                                onClick={() => { setShowApproveModal(false); setSelectedCourseId(null); setSelectedCourseName(''); }}
                            >
                                <Icon icon="fluent:dismiss-24-regular" width={24} />
                            </button>
                        </div>
                        <div className="p-8">
                            <p className="text-gray-600 leading-relaxed font-medium">
                                Are you sure you want to approve <span className="text-gray-900 font-black">&quot;{selectedCourseName}&quot;</span>? This will make the course live in the student catalog.
                            </p>
                        </div>
                        <div className="px-8 py-6 bg-gray-50/50 flex flex-col sm:flex-row gap-3">
                            <button
                                type="button"
                                className="flex-1 px-6 py-3.5 rounded-2xl border border-gray-200 text-gray-700 font-bold hover:bg-white transition-all text-sm"
                                onClick={() => { setShowApproveModal(false); setSelectedCourseId(null); setSelectedCourseName(''); }}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                className="flex-1 px-6 py-3.5 rounded-2xl bg-emerald-500 hover:bg-emerald-600 text-white font-bold transition-all shadow-lg shadow-emerald-200 flex items-center justify-center gap-2 text-sm"
                                onClick={handleApprove}
                                disabled={processing}
                            >
                                {processing ? (
                                    <>
                                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                        <span>Syncing...</span>
                                    </>
                                ) : (
                                    <>
                                        <Icon icon="fluent:checkmark-24-regular" width={20} />
                                        <span>Yes, Approve</span>
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Reject Modal Enhancement */}
            {showRejectModal && (
                <div className="fixed inset-0 bg-black/40 backdrop-blur-md flex items-center justify-center z-[70] p-4 animate-in fade-in duration-300">
                    <div className="bg-white rounded-[32px] shadow-2xl max-w-lg w-full overflow-hidden animate-in zoom-in-95 duration-300 border border-white/20">
                        <div className="px-8 py-6 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-xl bg-red-50 text-red-600 flex items-center justify-center">
                                    <Icon icon="fluent:dismiss-circle-24-regular" width={24} />
                                </div>
                                <h5 className="text-lg font-black text-gray-900 tracking-tight">Reject Submission</h5>
                            </div>
                            <button
                                type="button"
                                className="w-10 h-10 rounded-xl flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-all"
                                onClick={() => { setShowRejectModal(false); setSelectedCourseId(null); setSelectedCourseName(''); setRejectionReason(''); }}
                            >
                                <Icon icon="fluent:dismiss-24-regular" width={24} />
                            </button>
                        </div>
                        <div className="p-8">
                            <div className="mb-6 p-4 rounded-2xl bg-gray-50 border border-gray-100">
                                <p className="text-gray-600 font-medium text-sm">
                                    Confirming rejection for <span className="text-gray-900 font-black">&quot;{selectedCourseName}&quot;</span>.
                                </p>
                            </div>
                            
                            <label className="block text-[11px] font-black text-gray-400 uppercase tracking-widest mb-3 ml-1">Reason for Rejection *</label>
                            <textarea
                                className="w-full px-5 py-4 border border-gray-100 bg-gray-50/50 rounded-2xl text-sm font-medium focus:outline-none focus:ring-4 focus:ring-[#7083FF]/5 focus:border-[#7083FF]/30 transition-all placeholder:text-gray-300 resize-none"
                                rows="5"
                                value={rejectionReason}
                                onChange={(e) => setRejectionReason(e.target.value)}
                                placeholder="Explain why this course doesn't meet quality standards..."
                            />
                        </div>
                        <div className="px-8 py-6 bg-gray-50/50 flex flex-col sm:flex-row gap-3">
                            <button
                                type="button"
                                className="flex-1 px-6 py-3.5 rounded-2xl border border-gray-200 text-gray-700 font-bold hover:bg-white transition-all text-sm"
                                onClick={() => { setShowRejectModal(false); setSelectedCourseId(null); setSelectedCourseName(''); setRejectionReason(''); }}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                className="flex-1 px-6 py-3.5 rounded-2xl bg-red-500 hover:bg-red-600 text-white font-bold transition-all shadow-lg shadow-red-100 flex items-center justify-center gap-2 text-sm disabled:opacity-50"
                                onClick={handleReject}
                                disabled={processing || !rejectionReason.trim()}
                            >
                                {processing ? (
                                    <>
                                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                        <span>Processing...</span>
                                    </>
                                ) : (
                                    <>
                                        <Icon icon="fluent:dismiss-24-regular" width={20} />
                                        <span>Yes, Reject</span>
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

export default PendingApproval
